import * as React from 'react'
import styled from 'styled-components'
import * as constants from '../../static/constants'
import I18n from '../../core/i18n'
import Typography from 'components/atoms/_layouts/Typography'
import FavoriteButton from 'components/atoms/FavoriteButton'
import SvgIcon from 'components/atoms/SvgIcon'

interface IProps {
  post: any
  pickup?: boolean
  likeable?: boolean
  column?: boolean
  noMargin?: boolean
  onToggleLike?: (postId, like) => void
}

const PostCard: React.FC<IProps> = ({ post, pickup, likeable, column, noMargin, onToggleLike }) => {
  const getHref = () => {
    const baseUrl = `/posts/${post.slug}`
    const currentParams = window.location.search
    return currentParams ? `${baseUrl}${currentParams}` : baseUrl
  }

  return (
    <PostCardWrap
      pickup={pickup}
      href={getHref()}
      column={column}
      noMargin={noMargin}
      target="_blank"
    >
      <div className={likeable ? 'Image likeable' : 'Image'}>
        {post.post_image && post.post_image.image_url ? (
          <img
            src={post.post_image.image_url}
            alt={post.post_image.description}
            aria-hidden="true"
            loading="lazy"
          />
        ) : (
          <p className="NoImage">No Image</p>
        )}
        {likeable && (
          <div className="ToggleLike">
            <FavoriteButton post={post} onToggleLike={onToggleLike} />
          </div>
        )}
      </div>
      <div className="Info">
        <div className="Header">
          <div className="HeaderInner">
            {post.area && <div>{I18n.t('post.guided_tour', { area: post.area.name_i18n })}</div>}
          </div>
          <h3 className="Title">{post.name}</h3>
          <div className="Duration">{post.duration} hours</div>
        </div>
        <div className="Body">
          <div className="PostCard_Score">
            {post.reviews_count > 0 && (
              <>
                <div className="Score">
                  <SvgIcon name="star" className="icon" ariaHidden={true} role="presentation" />
                  <span className="score">{post.average_review_score}</span>
                </div>
                <Typography color="subtext" type="body" size="lg">
                  ({post.reviews_count.toLocaleString()})
                </Typography>
              </>
            )}
          </div>
          <div className="Price">
            <span className="subLabel">From</span>¥<span className="value">{post.price}</span>
            <span className="type">/ {post.post_type === 'group' ? 'person' : 'group'}</span>
          </div>
        </div>
      </div>
    </PostCardWrap>
  )
}

const PostCardWrap = styled.a`
  display: flex;
  flex-direction: column;
  width: ${({ pickup, column }) => (column ? '100%' : pickup ? '100%' : '100%')};
  color: ${constants.COLORS.black};
  flex-shrink: 0;
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 1rem;
  &:hover {
    .Image > img {
      transform: scale(1.1);
    }
  }

  .Image {
    position: relative;
    width: 100%;
    height: ${({ pickup, column }) => (pickup || column ? 'auto' : 'auto')};
    border-radius: 0.8rem 0.8rem 0 0;
    overflow: hidden;
    &.likeable::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 33%;
      background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
      z-index: 1;
    }

    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      height: ${({ column }) => (column ? 'auto' : 'auto')};
    }
    .ToggleLike {
      position: absolute;
      top: 1.2rem;
      right: 1.2rem;
      z-index: 2;
      .icon {
        width: 2.4rem;
        height: 2.4rem;
      }
    }
    > img {
      width: 100%;
      height: auto;
      object-fit: cover;
      aspect-ratio: 5 / 3;
      vertical-align: middle;
      transition: transform 0.3s ease;
    }
    .NoImage {
      width: 100%;
      height: 100%;
      height: inherit;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: ${constants.COLORS.infoBG};
    }
  }

  .Info {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 1.6rem;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin: ${({ pickup }) => (pickup ? '0' : '0')};
    }
  }
  .Header {
    .HeaderInner {
      display: flex;
      align-items: center;
      line-height: 2.4rem;
      color: ${constants.COLORS.subText};
      font-size: 1.4rem;
    }
    .instant-booking {
      color: ${constants.COLORS.useally};
      width: 2rem;
      height: 2rem;
    }
    .Title {
      width: 100%;
      line-height: 2.4rem;
      font-size: 1.5rem;
      font-weight: 700;
    }
    .Duration {
      margin-bottom: 0.4rem;
      line-height: 2.4rem;
      color: ${constants.COLORS.subText};
      font-size: 1.4rem;
    }
  }

  .Body {
    display: flex;
    flex-wrap: wrap;
  }

  .Price {
    margin-top: 0.4rem;
    line-height: 2.4rem;
    font-size: 1.4rem;
    text-transform: capitalize;
    color: ${constants.COLORS.black};
    .subLabel {
      font-weight: 700;
      margin-right: 0.4rem;
    }
    .value {
      font-size: 1.8rem;
      font-weight: 700;
    }
    .type {
      font-size: 1.2rem;
      margin-left: 0.4rem;
    }
  }

  .PostCard_Score {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .Score {
    display: flex;
    align-items: center;
    margin-right: 0.4rem;

    .icon {
      color: ${constants.COLORS.useally};
      width: 2rem;
      height: 2rem;
    }

    .score {
      font-size: 1.4rem;
      margin-left: 0.4rem;
      font-weight: 700;
    }
  }

  & + & {
    ${({ column, noMargin }) =>
      !column &&
      !noMargin &&
      `
      margin-left: 24px;
        @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
          margin-left: 12px;
        }
    `}
  }
`

export default PostCard
